// Music core + edge alignment (pure). YouTube videos often open/close with non-music (skits, talk,
// silence, credits) or with a differently-felt intro, where beat tracking is irregular.
//
//  1. reference values from the middle 60% of the song: beat P, beats per bar k, bar length D
//  2. each downbeat interval is "regular" when its beats are evenly spaced at P or a 3:2 / 4:3 / 2:1
//     relative of it and it lasts ~a bar (or half / double); a long steady stretch whose downbeats were
//     missed is regular too
//  3. music core = first … last run of ≥ 4 regular bars
//  4. inside the core, long downbeat-less stretches are split into k-beat bars (counted back from the
//     next real downbeat)
//  5. the edges outside the core get the core's bar grid extended to the start / end of the audio —
//     unless an edge holds its own steady beat at a clearly different tempo (a different piece of music)

const LEVELS = [1, 1.5, 2 / 3, 4 / 3, 3 / 4, 2, 0.5];
const median = (a: number[]) => { const s = [...a].sort((x, y) => x - y); return s.length ? s[Math.floor(s.length / 2)] : 0; };
const mode = (a: number[]) => {
  const c = new Map<number, number>();
  for (const v of a) c.set(v, (c.get(v) ?? 0) + 1);
  let best = 0, n = -1;
  for (const [v, k] of c) if (k > n) { n = k; best = v; }
  return best;
};
function spread(gaps: number[]) {
  const mean = gaps.reduce((s, x) => s + x, 0) / Math.max(1, gaps.length);
  const cv = gaps.length ? Math.sqrt(gaps.reduce((s, x) => s + (x - mean) ** 2, 0) / gaps.length) / (mean || 1) : 1;
  return { mean, cv };
}

interface Interval { s: number; e: number; beats: number[]; regular: boolean; long: boolean }
export interface MusicCore { P: number; k: number; D: number; start: number | null; end: number | null }

export function findMusicCore(beats: number[], downbeats: number[]): MusicCore & { ivs: Interval[] } {
  const db = [...downbeats].sort((x, y) => x - y);
  const n = beats.length;
  const mid = beats.slice(Math.floor(n * 0.2), Math.ceil(n * 0.8));
  const P = median(mid.slice(1).map((t, i) => t - mid[i]));
  const ivs: Interval[] = db.slice(0, -1).map((d, i) => ({
    s: d, e: db[i + 1], beats: beats.filter((b) => b >= d - 0.02 && b < db[i + 1] - 0.02), regular: false, long: false,
  }));
  const midIv = ivs.slice(Math.floor(ivs.length * 0.2), Math.ceil(ivs.length * 0.8));
  const k = mode(midIv.map((v) => v.beats.length).filter((x) => x >= 2));
  const D = median(midIv.filter((v) => v.beats.length === k).map((v) => v.e - v.s));
  if (!(P > 0 && k >= 2 && D > 0)) return { P, k, D, start: null, end: null, ivs };

  for (const v of ivs) {
    const gaps = v.beats.slice(1).map((t, q) => t - v.beats[q]).concat(v.beats.length ? [v.e - v.beats[v.beats.length - 1]] : []);
    if (gaps.length < 2) continue;
    const { mean, cv } = spread(gaps);
    const level = LEVELS.some((r) => Math.abs(mean / P - r) / r < 0.08);
    const barLike = [0.5, 1, 2].some((m) => Math.abs(v.e - v.s - m * D) <= 0.12 * m * D);
    if (cv < 0.2 && level && barLike) v.regular = true;
    else if (v.beats.length >= 2 * k && cv < 0.15 && Math.abs(mean / P - 1) < 0.08) { v.regular = true; v.long = true; }
  }
  // a run needs ≥ 4 bars; a long steady stretch counts as the bars it spans
  const weight = (v: Interval) => (v.long ? Math.max(1, Math.round((v.e - v.s) / D)) : 1);
  const runFrom = (order: number[]) => {
    let acc = 0, first = -1;
    for (const i of order) {
      if (!ivs[i].regular) { acc = 0; first = -1; continue; }
      if (first < 0) first = i;
      acc += weight(ivs[i]);
      if (acc >= 4) return first;
    }
    return -1;
  };
  const idx = ivs.map((_, i) => i);
  const a = runFrom(idx), z = runFrom([...idx].reverse());
  return { P, k, D, start: a >= 0 ? ivs[a].s : null, end: z >= 0 ? ivs[z].e : null, ivs };
}

export interface AlignedGrid { beats: number[]; downbeats: number[]; musicStart: number | null; musicEnd: number | null }

export function alignToCore(beats: number[], downbeats: number[], end: number): AlignedGrid {
  const c = findMusicCore(beats, downbeats);
  if (c.start == null || c.end == null || c.end <= c.start) return { beats, downbeats, musicStart: null, musicEnd: null };
  const { k, P } = c;
  let B = beats.slice(), DB = downbeats.slice();

  // long steady stretches inside the core: bars of k beats, counted back from the next real downbeat
  for (const v of c.ivs) {
    if (!v.long || v.s < c.start - 0.02 || v.e > c.end + 0.02) continue;
    for (let q = v.beats.length - k; q > 0; q -= k) DB.push(v.beats[q]);
  }

  // bar length right at each core edge (first / last regular bars of ~D)
  const near = (list: Interval[]) => median(list.filter((v) => v.regular && !v.long && Math.abs(v.e - v.s - c.D) <= 0.12 * c.D).slice(0, 4).map((v) => v.e - v.s)) || c.D;
  const inCore = c.ivs.filter((v) => v.s >= c.start! - 0.02 && v.e <= c.end! + 0.02);
  const headBar = near(inCore), tailBar = near([...inCore].reverse());

  // an edge that is steady music at a clearly different (non 3:2 / 2:1) tempo keeps its own beats
  const otherMusic = (bs: number[]) => {
    if (bs.length < 8) return false;
    const { mean, cv } = spread(bs.slice(1).map((t, i) => t - bs[i]));
    return cv < 0.15 && !LEVELS.some((r) => Math.abs(mean / P - r) / r < 0.08);
  };

  const head = B.filter((t) => t < c.start! - 0.02);
  if (head.length && !otherMusic(head)) {
    B = B.filter((t) => t >= c.start! - 0.02);
    DB = DB.filter((t) => t >= c.start! - 0.02);
    const step = headBar / k;
    for (let j = 1; c.start - j * step >= -1e-6; j++) {
      const t = c.start - j * step;
      B.push(t);
      if (j % k === 0) DB.push(t);
    }
  }
  const tail = B.filter((t) => t > c.end! + 0.02);
  if (!otherMusic(tail)) {
    B = B.filter((t) => t <= c.end! + 0.02);
    DB = DB.filter((t) => t <= c.end! + 0.02);
    if (!B.some((t) => Math.abs(t - c.end!) < 0.02)) B.push(c.end);
    if (!DB.some((t) => Math.abs(t - c.end!) < 0.02)) DB.push(c.end);
    const step = tailBar / k;
    for (let j = 1; c.end + j * step <= end - 0.05; j++) {
      const t = c.end + j * step;
      B.push(t);
      if (j % k === 0) DB.push(t);
    }
  }
  const uniq = (a: number[]) => a.sort((x, y) => x - y).filter((t, i, arr) => i === 0 || t - arr[i - 1] > 0.01);
  return { beats: uniq(B), downbeats: uniq(DB), musicStart: c.start, musicEnd: c.end };
}
